import React from 'react';
import { Table, Button } from 'react-bootstrap';
import { MonthResponse } from '../../../../services/agent-report/response/MonthResponse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

interface AgentTableReportYearProps {
    data: MonthResponse;
    startDate: string;
    endDate: string;
}

const AgentTableReportYear: React.FC<AgentTableReportYearProps> = ({ data, startDate, endDate }) => {
    // Convert start and end dates to Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Generate an array of months between startDate and endDate
    const monthRange: string[] = [];
    let current = new Date(start);
    while (current <= end) {
        monthRange.push(current.toLocaleString("en-US", { month: "long", year: "numeric" }));
        current.setMonth(current.getMonth() + 1);
    }

    // Group data by month
    const groupedData = data.data.reduce((acc, item) => {
        const date = new Date(item.month);
        const monthYear = date.toLocaleString("en-US", { month: "long", year: "numeric" });

        if (!acc[monthYear]) {
            acc[monthYear] = { totalBookings: 0, agents: {} };
        }

        acc[monthYear].totalBookings += item.total_booking;
        acc[monthYear].agents[item.name] = (acc[monthYear].agents[item.name] || 0) + item.total_booking;

        return acc;
    }, {} as Record<string, { totalBookings: number; agents: Record<string, number> }>);

    // Extract unique agent names
    const agentNames = Array.from(new Set(data.data.map(item => item.name)));

    // Compute total yearly bookings for each agent
    const yearlyTotals = agentNames.reduce((acc, agent) => {
        acc[agent] = Object.values(groupedData).reduce((sum, { agents }) => sum + (agents[agent] || 0), 0);
        return acc;
    }, {} as Record<string, number>);

    // Compute total bookings for the entire year
    const totalYearlyBookings = Object.values(groupedData).reduce((sum, { totalBookings }) => sum + totalBookings, 0);

    // Function to export PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Agent Report - Yearly", 14, 10);

        const tableData = agentNames.map(agent => [
            agent,
            ...monthRange.map(monthYear => {
                const monthData = groupedData[monthYear] || { totalBookings: 0, agents: {} };
                const agentBookings = monthData.agents[agent] || 0;
                return monthData.totalBookings > 0
                    ? ((agentBookings / monthData.totalBookings) * 100).toFixed(2) + "%"
                    : "-";
            }),
            totalYearlyBookings > 0 ? ((yearlyTotals[agent] / totalYearlyBookings) * 100).toFixed(2) + "%" : "-"
        ]);

        autoTable(doc, {
            head: [["Agent", ...monthRange, "Year"]],
            body: tableData,
        });

        doc.save("AgentReport.pdf");
    };

    // Function to export Excel
    const exportToExcel = () => {
        const wsData = [
            ["Agent", ...monthRange, "Year"],
            ...agentNames.map(agent => [
                agent,
                ...monthRange.map(monthYear => {
                    const monthData = groupedData[monthYear] || { totalBookings: 0, agents: {} };
                    const agentBookings = monthData.agents[agent] || 0;
                    return monthData.totalBookings > 0
                        ? ((agentBookings / monthData.totalBookings) * 100).toFixed(2) + "%"
                        : "-";
                }),
                totalYearlyBookings > 0 ? ((yearlyTotals[agent] / totalYearlyBookings) * 100).toFixed(2) + "%" : "-"
            ])
        ];

        const ws = XLSX.utils.aoa_to_sheet(wsData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, "AgentReport.xlsx");
    };

    return (
        <div className="mt-4">
            <div className="mb-3 d-flex gap-2">
                <Button variant="primary" onClick={exportToPDF}>Export to PDF</Button>
                <Button variant="success" onClick={exportToExcel}>Export to Excel</Button>
            </div>

            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Agent</th>
                            {monthRange.map((monthYear, index) => (
                                <th key={index}>{monthYear}</th>
                            ))}
                            <th>Year</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agentNames.map(agent => (
                            <tr key={agent + '-percentage'}>
                                <td>{agent}</td>
                                {monthRange.map((monthYear, index) => {
                                    const monthData = groupedData[monthYear] || { totalBookings: 0, agents: {} };
                                    const agentBookings = monthData.agents[agent] || 0;
                                    const percentage = monthData.totalBookings > 0
                                        ? ((agentBookings / monthData.totalBookings) * 100).toFixed(2)
                                        : "-";
                                    return <td key={index}>{percentage !== "-" ? `${percentage}%` : "-"}</td>;
                                })}
                                <td>
                                    {totalYearlyBookings > 0
                                        ? ((yearlyTotals[agent] / totalYearlyBookings) * 100).toFixed(2) + "%"
                                        : "-"}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default AgentTableReportYear;